import { useMemo } from 'react';

/**
 * useCommentPermissions Hook
 *
 * Determines whether the current user can edit or delete a comment.
 * Permission rules:
 * - Must be the comment owner (matched by email via unified users table)
 * - Request must not have ended (except additional docs created after end)
 * - Request progress must be active
 *
 * @param {Object} params
 * @param {Object} params.comment - The comment object (with user association from unified users table)
 * @param {Object} params.requestProgress - Active request progress record
 * @param {string|null} params.requestDateEnded - Request date_ended value
 * @param {boolean} params.isAdditionalDocument - Whether this is an additional document comment
 * @param {string} params.currentUserEmail - Current user's email for ownership check
 * @returns {{ canEdit: boolean, canDelete: boolean }}
 */
const useCommentPermissions = ({
  comment,
  requestProgress,
  requestDateEnded,
  isAdditionalDocument = false,
  currentUserEmail,
}) => {
  return useMemo(() => {
    if (!comment || !currentUserEmail)
      return { canEdit: false, canDelete: false };

    // Check ownership by matching email (unified users table)
    const commentAuthorEmail = comment.user?.email || null;
    const isOwner =
      commentAuthorEmail &&
      commentAuthorEmail.toLowerCase() === currentUserEmail.toLowerCase();

    if (!isOwner) return { canEdit: false, canDelete: false };

    // Additional documents created after request ended are always editable
    if (isAdditionalDocument && requestDateEnded) {
      const createdAfterEnd =
        new Date(comment.createdAt) > new Date(requestDateEnded);
      if (createdAfterEnd) return { canEdit: true, canDelete: true };
    }

    // Cannot edit/delete if request has ended
    if (requestDateEnded) return { canEdit: false, canDelete: false };

    // Must be at active stage
    if (!requestProgress?.active) return { canEdit: false, canDelete: false };

    return { canEdit: true, canDelete: true };
  }, [
    comment,
    requestProgress,
    requestDateEnded,
    isAdditionalDocument,
    currentUserEmail,
  ]);
};

export default useCommentPermissions;
